import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown, Clock3, MapPin, Phone } from "lucide-react";
import { images } from "./data";

const lines = ["Good Food.", "Good Mood.", "Royal Experience."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-svh flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={images.breakfastSpread}
          alt="Club sandwich, cheese omelette and golden fries at The Royal Cafe, Anjuna"
          className="h-full w-full scale-110 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-onyx/85 via-onyx/55 to-onyx" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,9,8,0.5)_100%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-14 pt-36 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-7 flex w-max max-w-full items-center gap-3 overflow-hidden rounded-full border border-gold/30 bg-onyx/50 px-4 py-2 backdrop-blur-md"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
          <span className="whitespace-nowrap font-mono text-[0.55rem] uppercase tracking-[0.18em] text-gold-light sm:text-[0.65rem] sm:tracking-[0.3em]">Est. 2017 · Anjuna Beach Road, North Goa</span>
        </motion.p>

        <h1 className="font-display text-4xl leading-[1.14] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "gold-text italic" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-xl text-base leading-relaxed text-ash sm:text-lg"
        >
          Your destination for delicious breakfast, lunch and dinner in North Goa — South Indian tiffins, North Indian
          curries, continental plates and cafe classics, served in a welcoming royal atmosphere.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#menu"
            data-testid="hero-menu-button"
            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-deep px-7 py-3.5 text-sm font-semibold tracking-wide text-onyx shadow-[0_0_28px_rgba(212,175,55,0.3)] transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(212,175,55,0.5)]"
          >
            Explore the Menu
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="tel:+917358403972"
            data-testid="hero-call-button"
            className="flex items-center gap-2 rounded-full border border-cream/25 px-7 py-3.5 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold-light"
          >
            <Phone size={15} /> +91 73584 03972
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="relative mx-auto flex w-full max-w-7xl flex-wrap items-center gap-x-8 gap-y-2 border-t border-cream/10 px-5 py-5 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-ash sm:px-8"
        data-testid="hero-info-bar"
      >
        <span className="flex items-center gap-2"><MapPin size={13} className="text-gold" /> Anjuna, North Goa</span>
        <span className="flex items-center gap-2"><Clock3 size={13} className="text-gold" /> Open daily · 8 AM — 11 PM</span>
        <span className="ml-auto flex items-center gap-2 text-gold-light/70">Scroll to savour <ChevronDown size={13} className="animate-bounce" /></span>
      </motion.div>
    </section>
  );
};
